/**
 * Phase COI-PREVIEW — shared modal that previews the agent email
 * before any COI-send action and dispatches the live send on confirm.
 *
 * Used by three entry points on AdminRentalDetailPage:
 *   • mode="standard_resend"  → POST /coi-request/resend
 *   • mode="imported"         → POST /coi-imported-request
 *   • mode="with_file"        → identical to standard_resend BUT shows
 *                               the "COI already on file" callout above
 *                               the body preview (Phase COIWITHFILE).
 *                               Imported + on-file → routes to
 *                               /coi-imported-request internally.
 *
 * Props:
 *   open          boolean
 *   onOpenChange  (next: boolean) => void
 *   rental        rental object (must have id, imported, value)
 *   customer      customer object (for the gate hints / on-file callout)
 *   coiRequest    rental.coi_request subdoc (for on-file callout details)
 *   mode          "standard_resend" | "imported" | "with_file"
 *   coiTemplates  array of available templates (passed through to picker)
 *   initialTemplateId  string|null — preselected pick
 *   onTemplateChange   (id: string|null) => void — keeps parent in sync
 *   onSent             () => Promise<void> — called after a successful send
 *                       (parent's existing ``reload``)
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, AlertTriangle, Paperclip, FileText, Loader2, FileWarning } from "lucide-react";

const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtMoney = (cents) => {
    if (cents == null) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency", currency: "USD", maximumFractionDigits: 0,
    }).format(Number(cents));
};

export function CoiSendPreviewModal({
    open, onOpenChange, rental, customer, coiRequest,
    mode, coiTemplates = [], initialTemplateId, onTemplateChange, onSent,
}) {
    const [templateId, setTemplateId] = useState(initialTemplateId || null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    // Phase COI-PREVIEW-INLINE — inline rendering of the attached COI
    // request PDF below the email body. Re-fetches on template change so
    // admins can A/B compare templates without the new-tab roundtrip.
    // ``pdfRequestSeq`` guards against out-of-order responses when the
    // picker is rattled quickly: only the latest issued request commits
    // its blob URL.
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const pdfRequestSeq = useRef(0);

    // Reset internal template when modal reopens with a new initial pick.
    useEffect(() => {
        if (open) setTemplateId(initialTemplateId || null);
    }, [open, initialTemplateId]);

    const fetchPreview = useCallback(async () => {
        if (!rental?.id) return;
        setLoading(true);
        setFetchError(null);
        try {
            const resp = await api.post(
                `/admin/rentals/${rental.id}/coi-request/preview`,
                { template_id: templateId || null },
            );
            setPreview(resp.data);
        } catch (err) {
            setFetchError(formatApiError(err));
            setPreview(null);
        } finally {
            setLoading(false);
        }
    }, [rental?.id, templateId]);

    useEffect(() => {
        if (open) fetchPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, templateId]);

    // Phase COI-PREVIEW-INLINE — keep the inline PDF in sync with the
    // selected template. Re-fetch on every template change, debounced
    // by 200ms so a quick rattle of the picker only fires the last
    // selection. Each fetch grabs a sequence number; only the latest
    // sequence commits its blob URL, preventing race-condition stutter.
    const fetchPreviewPdf = useCallback(async () => {
        if (!rental?.id) return;
        const seq = ++pdfRequestSeq.current;
        setPdfLoading(true);
        setPdfError(null);
        try {
            const resp = await api.post(
                `/admin/rentals/${rental.id}/coi-request/preview-pdf`,
                { template_id: templateId || null },
                { responseType: "blob" },
            );
            if (seq !== pdfRequestSeq.current) return; // stale
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            setPdfBlobUrl((prev) => {
                if (prev) {
                    try { URL.revokeObjectURL(prev); } catch (_) { /* ignore */ }
                }
                return url;
            });
        } catch (err) {
            if (seq !== pdfRequestSeq.current) return; // stale
            // Blob-typed axios errors carry the JSON body inside resp.data
            // as a Blob — try to read it for a friendlier message.
            let msg = formatApiError(err);
            try {
                const blob = err?.response?.data;
                if (blob && typeof blob.text === "function") {
                    const text = await blob.text();
                    const parsed = JSON.parse(text);
                    if (parsed?.detail) msg = parsed.detail;
                }
            } catch (_) { /* fall through with formatApiError */ }
            setPdfError(msg);
        } finally {
            if (seq === pdfRequestSeq.current) setPdfLoading(false);
        }
    }, [rental?.id, templateId]);

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => { fetchPreviewPdf(); }, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, templateId, rental?.id]);

    // Clean up the blob URL on unmount + when the modal closes.
    useEffect(() => {
        if (!open && pdfBlobUrl) {
            try { URL.revokeObjectURL(pdfBlobUrl); } catch (_) { /* ignore */ }
            setPdfBlobUrl(null);
        }
        return () => {
            if (pdfBlobUrl) {
                try { URL.revokeObjectURL(pdfBlobUrl); } catch (_) { /* ignore */ }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onPickTemplate = (id) => {
        setTemplateId(id);
        onTemplateChange?.(id);
    };

    const onSend = async () => {
        if (!preview || (preview.gate_errors || []).length) return;
        setSending(true);
        try {
            // Imported rentals (or the with-file flow on an imported rental)
            // go through the imported endpoint so the same template-id
            // writeback bookkeeping applies. Everything else uses the
            // canonical resend endpoint.
            const useImportedEndpoint = mode === "imported"
                || (mode === "with_file" && rental.imported);
            const path = useImportedEndpoint
                ? `/admin/rentals/${rental.id}/coi-imported-request`
                : `/admin/rentals/${rental.id}/coi-request/resend`;
            await api.post(path, { template_id: templateId || null });
            toast.success(mode === "with_file" ? "Resent" : "COI request sent");
            onOpenChange(false);
            await onSent?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    // Phase COI-PREVIEW Fix 2 — render the attached PDF inline so
    // admins can see exactly what the template controls. The endpoint
    // streams the same PDF that would be attached on Send; nothing is
    // persisted, no upload token is minted.
    const onViewAttachedPdf = async () => {
        if (!rental?.id) return;
        try {
            const resp = await api.post(
                `/admin/rentals/${rental.id}/coi-request/preview-pdf`,
                { template_id: templateId || null },
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (err) {
            toast.error(await import("@/lib/api").then((m) => m.formatBlobApiError(err)));
        }
    };

    const gateErrors = preview?.gate_errors || [];
    const canSend = !!preview && gateErrors.length === 0 && !sending && !loading;
    const showOnFileCallout = mode === "with_file" && coiRequest?.response_coi_filename;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[92vh] overflow-y-auto"
                data-testid="coi-send-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send size={18} className="text-blue-700" />
                        Preview COI request email
                    </DialogTitle>
                    <DialogDescription>
                        Review the exact message the insurance agent will receive.
                        The COI request PDF is attached automatically.
                    </DialogDescription>
                </DialogHeader>

                {/* Phase COIWITHFILE callout */}
                {showOnFileCallout && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                        data-testid="coi-preview-on-file-callout">
                        A COI is already on file
                        {coiRequest.response_coi_filename
                            ? <> (<strong>{coiRequest.response_coi_filename}</strong>)</>
                            : null}
                        {coiRequest.response_received_at
                            ? <>, received {fmt(coiRequest.response_received_at)}</>
                            : null}
                        . Sending will email the agent for a refreshed certificate.
                        The existing COI stays attached until the new one arrives.
                    </div>
                )}

                {/* Header rows: from / to / subject */}
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5"
                    data-testid="coi-preview-headers">
                    <HeaderRow label="From">
                        <span data-testid="coi-preview-from">
                            {preview?.from_display
                                ? <>{preview.from_display} &lt;{preview.from_email}&gt;</>
                                : (preview?.from_email || "—")}
                        </span>
                    </HeaderRow>
                    <HeaderRow label="To">
                        <span data-testid="coi-preview-to">
                            {preview?.to_name
                                ? <>{preview.to_name} &lt;{preview.to_email}&gt;</>
                                : (preview?.to_email || customer?.agent_email || "—")}
                        </span>
                    </HeaderRow>
                    {preview?.cc && (
                        <HeaderRow label="Cc">
                            <span data-testid="coi-preview-cc">{preview.cc}</span>
                        </HeaderRow>
                    )}
                    <HeaderRow label="Subject">
                        <span className="font-medium text-slate-900"
                            data-testid="coi-preview-subject">
                            {preview?.subject || (loading ? "Rendering…" : "—")}
                        </span>
                    </HeaderRow>
                    {preview?.attachment_filename && (
                        <HeaderRow label="Attachment">
                            <span className="inline-flex items-center gap-1 text-slate-700"
                                data-testid="coi-preview-attachment">
                                <Paperclip size={12} /> {preview.attachment_filename}
                                <button type="button"
                                    onClick={onViewAttachedPdf}
                                    className="ml-2 text-blue-900 hover:underline text-[11px]"
                                    data-testid="coi-preview-attachment-view">
                                    Preview PDF →
                                </button>
                            </span>
                        </HeaderRow>
                    )}
                </div>

                {/* Body preview / gate-failure state */}
                {fetchError && (
                    <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                        data-testid="coi-preview-fetch-error">
                        Could not load preview: {fetchError}
                    </div>
                )}
                {!fetchError && gateErrors.length > 0 && (
                    <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                        data-testid="coi-preview-gate-errors">
                        <div className="flex items-center gap-1.5 font-semibold mb-1">
                            <AlertTriangle size={14} /> Cannot send yet
                        </div>
                        <ul className="list-disc pl-5 space-y-0.5">
                            {gateErrors.map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {!fetchError && gateErrors.length === 0 && (
                    <div className="rounded-md border border-slate-200 bg-white overflow-hidden"
                        data-testid="coi-preview-body">
                        {preview?.body_html ? (
                            <iframe
                                title="COI email body preview"
                                sandbox=""
                                srcDoc={`<!doctype html><html><body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;line-height:1.5;padding:14px;color:#0f172a;background:#fff;">${preview.body_html}</body></html>`}
                                className="block w-full h-72"
                                data-testid="coi-preview-body-iframe"
                            />
                        ) : preview?.body_text ? (
                            <pre className="m-0 p-3 text-xs whitespace-pre-wrap font-mono text-slate-800"
                                data-testid="coi-preview-body-text">
                                {preview.body_text}
                            </pre>
                        ) : (
                            <div className="p-3 text-xs text-slate-500"
                                data-testid="coi-preview-body-loading">
                                {loading ? "Rendering preview…" : "—"}
                            </div>
                        )}
                    </div>
                )}

                {/* Phase COI-PREVIEW-INLINE — inline rendering of the
                    attached COI request PDF. Re-fetches on template
                    change so the surface always reflects the picker. */}
                {!fetchError && gateErrors.length === 0 && (
                    <div className="space-y-1.5" data-testid="coi-preview-inline-pdf-section">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
                                <Paperclip size={12} /> Attached PDF preview
                            </div>
                            {preview?.template_name && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200 px-2 py-0.5 text-[10px] font-semibold"
                                    data-testid="coi-preview-inline-pdf-template-chip">
                                    <FileText size={10} />
                                    {preview.template_name}
                                </span>
                            )}
                        </div>
                        <div className="relative rounded-md border border-slate-200 bg-slate-50 overflow-hidden"
                            style={{ height: 600 }}
                            data-testid="coi-preview-inline-pdf-container">
                            {pdfLoading && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50/85 text-xs text-slate-600"
                                    data-testid="coi-preview-inline-pdf-loading">
                                    <Loader2 size={18} className="animate-spin" />
                                    Rendering PDF…
                                </div>
                            )}
                            {!pdfLoading && pdfError && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center bg-red-50 text-red-900"
                                    data-testid="coi-preview-inline-pdf-error">
                                    <FileWarning size={22} />
                                    <p className="text-sm font-semibold">Could not load PDF preview</p>
                                    <p className="text-xs text-red-700/80 max-w-sm">{pdfError}</p>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="mt-1"
                                        onClick={fetchPreviewPdf}
                                        data-testid="coi-preview-inline-pdf-retry">
                                        Retry
                                    </Button>
                                </div>
                            )}
                            {!pdfError && pdfBlobUrl && (
                                <iframe
                                    title="COI request PDF preview"
                                    src={pdfBlobUrl}
                                    className="block w-full h-full bg-white"
                                    data-testid="coi-preview-inline-pdf-iframe"
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Chips: template + replacement value */}
                <div className="flex flex-wrap gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200 px-2 py-0.5"
                        data-testid="coi-preview-template-chip">
                        <FileText size={11} />
                        Template: <strong className="ml-0.5">{preview?.template_name || "—"}</strong>
                        {preview?.used_fallback && (
                            <span className="ml-1 text-amber-700">(fallback)</span>
                        )}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-900 border border-emerald-200 px-2 py-0.5"
                        data-testid="coi-preview-pdl-chip">
                        Physical damage limit:
                        <strong className="ml-0.5">
                            {preview?.physical_damage_limit || "—"}
                        </strong>
                        {preview?.rental_replacement_value != null && (
                            <span className="ml-1 text-emerald-700/70">
                                (from rental value {fmtMoney(preview.rental_replacement_value)})
                            </span>
                        )}
                    </span>
                </div>

                {/* Template picker — same component the parent renders inline */}
                {coiTemplates.length > 0 && (
                    <div className="space-y-1" data-testid="coi-preview-template-picker">
                        <label className="text-xs text-slate-600">
                            Change template (re-renders the preview):
                        </label>
                        <select
                            value={templateId || ""}
                            onChange={(e) => onPickTemplate(e.target.value || null)}
                            disabled={sending}
                            className="block w-full max-w-md rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            data-testid="coi-preview-template-select"
                        >
                            {coiTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{t.is_default ? "  (default)" : ""}
                                </option>
                            ))}
                        </select>
                        {/* Phase COI-PREVIEW Fix 2 — clarify the design intent:
                            template controls the ATTACHED PDF (cert holder,
                            coverage limits, etc.), not the email body itself. */}
                        <p className="text-[11px] text-slate-500"
                            data-testid="coi-preview-template-hint">
                            The selected template determines the attached PDF
                            (certificate holder, coverage limits, etc.) — not
                            the email body itself. Click <strong>Preview PDF →</strong>
                            above to see what the agent will receive.
                        </p>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        disabled={sending}
                        data-testid="coi-preview-cancel">
                        Cancel
                    </Button>
                    <Button
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        onClick={onSend}
                        disabled={!canSend}
                        title={!canSend && gateErrors.length
                            ? "Fix the issues above to enable send."
                            : undefined}
                        data-testid="coi-preview-send">
                        <Send size={14} /> {sending ? "Sending…" : "Send"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function HeaderRow({ label, children }) {
    return (
        <div className="flex gap-2">
            <span className="w-20 shrink-0 text-slate-500">{label}:</span>
            <span className="text-slate-800">{children}</span>
        </div>
    );
}

export default CoiSendPreviewModal;
